import React, { useState, useEffect } from 'react';
import Lottie from 'lottie-react';
import { useData } from '../context/DataContext';
import { ArrowRight, Code, Layout, Search, Server, CheckCircle2, ThumbsUp, ShieldCheck, Star, Zap, ChevronLeft, ChevronRight } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { SERVICE_CATEGORIES } from '../constants';
import { useDataStore } from '../store/dataStore';
import SEO from '../components/SEO';
import FadeIn from '../components/FadeIn';

const Services: React.FC = () => {
  const navigate = useNavigate();
  const { serviceCategories } = useDataStore();
  const { t } = useTranslation();
  const [lottieData, setLottieData] = useState<any>(null);
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);

  useEffect(() => {
    fetch('https://lottie.host/845a30ff-22f4-4db8-8355-473d255db72f/pjOfucY86R.json')
      .then(response => response.json())
      .then(data => setLottieData(data))
      .catch(error => console.error('Error loading Lottie animation:', error));
  }, []);

  const handleNextTab = () => {
    setActiveCategoryIndex((prev) => (prev + 1) % pricingCategories.length);
  };

  const handlePrevTab = () => {
    setActiveCategoryIndex((prev) => (prev - 1 + pricingCategories.length) % pricingCategories.length);
  };

  const pricingCategories = serviceCategories.slice(0, 3);
  const currentCategory = pricingCategories[activeCategoryIndex];
  const currentPlans = currentCategory?.packages || [];

  return (
    <div className="w-full bg-brand-light">
      <SEO
        pageCode="services"
        title="Hizmetlerimiz"
        description="Web tasarım, mobil uygulama, e-ticaret ve yapay zeka çözümleri gibi profesyonel yazılım hizmetlerimiz."
        link="/hizmetlerimiz"
      />
      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-brand-dark text-center">
        <FadeIn className="container mx-auto px-4 md:px-8">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            {t('services.heroTitle')}
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {t('services.heroDesc')}
          </p>
        </FadeIn>
      </section>

      {/* Innovation Section (Moved Up) */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <FadeIn className="w-full lg:w-1/2" direction="left">
              <div className="relative">
                <div className="absolute -top-10 -left-10 w-32 h-32 bg-brand-blue/10 rounded-full blur-2xl"></div>
                <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-brand-purple/10 rounded-full blur-2xl"></div>
                {lottieData && (
                  <Lottie
                    animationData={lottieData}
                    loop={true}
                    className="w-full max-w-lg mx-auto"
                  />
                )}
              </div>
            </FadeIn>
            <FadeIn className="w-full lg:w-1/2" direction="right" delay={0.2}>
              <div className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-blue/10 rounded-full text-brand-blue font-bold text-sm mb-6 shadow-lg shadow-brand-blue/10">
                <Zap size={16} />
                <span>{t('services.digitalSolutions')}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-brand-dark mb-6 leading-tight">
                {t('services.innovationTitle').split('\n')[0]} <br />
                <span className="text-brand-blue">{t('services.innovationTitle').split('\n')[1]?.trim()}</span>
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-8">
                {t('services.innovationDesc')}
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SERVICE_CATEGORIES.map((service, idx) => {
              const IconComponent = (LucideIcons as any)[service.icon] || LucideIcons.Layout;
              return (
              <FadeIn key={service.id} delay={idx * 0.1}>
                <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 hover:border-brand-blue/30 transition-all duration-300 group h-full">
                  <div className="w-14 h-14 bg-brand-blue/10 rounded-xl flex items-center justify-center text-brand-blue mb-6 group-hover:bg-brand-blue group-hover:text-white transition-colors">
                    <IconComponent size={28} />
                  </div>
                  <h3 className="text-2xl font-bold text-brand-dark mb-4">{service.title}</h3>
                  <p className="text-gray-700 mb-6 min-h-[48px]">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {service.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-gray-500 text-sm">
                        <div className="w-1.5 h-1.5 bg-brand-blue rounded-full"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => navigate('/iletisim')}
                    className="w-full py-3 rounded-lg border border-brand-blue text-brand-blue font-bold hover:bg-brand-blue hover:text-white transition-all flex items-center justify-center gap-2 mt-auto"
                  >
                    Teklif Al <ArrowRight size={18} />
                  </button>
                </div>
              </FadeIn>
              );
            })}
          </div>
        </div>
      </section>
      {/* AEO / GEO Optimization Table */}
      <section className="py-24 bg-brand-light">
        <div className="container mx-auto px-4 md:px-8">
          <FadeIn className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-purple-400 inline-block mb-4">
              Neden AEO ve GEO Optimizasyonu?
            </h2>
            <p className="text-gray-700 max-w-2xl mx-auto mt-6">
              Arama motorları artık sadece anahtar kelimeleri değil, "cevapları" indeksliyor. Pato Yazılım olarak web projelerinizi yeni nesil yapay zeka botlarına ve sesli arama (Voice Search) trendlerine tam uyumlu olarak inşa ediyoruz.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} className="max-w-5xl mx-auto overflow-x-auto">
            <table className="w-full text-left border-collapse rounded-xl overflow-hidden shadow-lg border border-gray-100">
              <thead>
                <tr className="bg-brand-dark text-white">
                  <th className="p-6 font-semibold text-lg border-b border-gray-700 w-1/3">Özellik</th>
                  <th className="p-6 font-semibold text-lg border-b border-gray-700 w-1/3 bg-gray-800">Geleneksel Web Siteleri</th>
                  <th className="p-6 font-semibold text-lg border-b border-gray-700 w-1/3 bg-brand-blue">Pato Yazılım (AEO/GEO Optimizasyonlu)</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="p-6 font-medium text-brand-dark">İçerik Mimarisi</td>
                  <td className="p-6 text-gray-600">Uzun, edebi ve laf kalabalığı olan paragraflar.</td>
                  <td className="p-6 text-gray-800 font-medium">Ters Piramit tekniğiyle, net cevabın ilk paragrafta verildiği yapılandırılmış içerikler.</td>
                </tr>
                <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="p-6 font-medium text-brand-dark">Soru & Cevap (SSS)</td>
                  <td className="p-6 text-gray-600">Sadece metin olarak yazılmış, arama motorlarının okuyamadığı yapılar.</td>
                  <td className="p-6 text-gray-800 font-medium">Arka planda FAQPage JSON-LD ile işaretlenmiş, Google "Sıfırıncı Sıra" (Featured Snippet) uyumlu bloklar.</td>
                </tr>
                <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="p-6 font-medium text-brand-dark">Sesli Arama (Voice Search)</td>
                  <td className="p-6 text-gray-600">Robotik ve aşırı resmi bir dil, mobil aramalarda başarısız sonuçlar.</td>
                  <td className="p-6 text-gray-800 font-medium">Doğal konuşma diline (Semantik SEO) yakın, kullanıcıların arama kalıplarına tam oturan içerikler.</td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="p-6 font-medium text-brand-dark">Veri Sunumu</td>
                  <td className="p-6 text-gray-600">Karmaşık verilerin düz metin içine yığılması.</td>
                  <td className="p-6 text-gray-800 font-medium">Botların kolayca çekebileceği HTML Tabloları (Table) ve Madde İşaretli Listelerle (List) desteklenmiş zengin yapılar.</td>
                </tr>
              </tbody>
            </table>
          </FadeIn>
        </div>
      </section>


      {/* Process Section */}
      <section className="py-24 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4 md:px-8">
          <FadeIn className="text-center mb-16">
            <h2 className="text-4xl font-bold text-brand-dark mb-4">{t('services.processTitle')}</h2>
            <p className="text-gray-700">{t('services.processDesc')}</p>
          </FadeIn>

          <div className="grid md:grid-cols-4 gap-8 relative">
            {/* Connecting Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-gray-200 -z-10 transform translate-y-1/2"></div>

            {(t('services.processSteps', { returnObjects: true }) as any[]).map((step: any, idx: number) => {
              const Icon = [Search, Layout, Code, Server][idx];
              return (
                <FadeIn key={idx} delay={idx * 0.1} className="bg-white p-6 text-center group h-full">
                  <div className="w-24 h-24 bg-white rounded-full border-4 border-white shadow-lg flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 relative z-10">
                    <Icon size={32} className="text-brand-blue" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-dark mb-2">{step.title}</h3>
                  <p className="text-gray-500 text-sm">
                    {step.desc}
                  </p>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-brand-light border-t border-gray-100">
        <div className="container mx-auto px-4 md:px-8">
          <FadeIn className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-4xl font-bold text-brand-dark mb-4">
              {currentCategory?.title} Paketleri
            </h2>
            <p className="text-gray-700">{t('common.pricingDesc')}</p>
          </FadeIn>

          {/* Pricing Toggle Slider */}
          <FadeIn className="flex justify-center items-center mb-16 gap-4" delay={0.1}>
            <button
              onClick={handlePrevTab}
              className="hidden md:flex w-10 h-10 rounded-full bg-white shadow-md items-center justify-center text-brand-blue hover:bg-brand-blue hover:text-white transition-all"
            >
              <ChevronLeft size={24} />
            </button>

            <div className="bg-white p-1.5 rounded-full shadow-lg border border-gray-100 flex relative overflow-hidden">
              <div
                className="absolute top-1.5 bottom-1.5 w-[110px] md:w-[180px] bg-brand-blue rounded-full transition-all duration-300 shadow-sm"
                style={{ transform: `translateX(${activeCategoryIndex * 100}%)` }}
              ></div>

              {pricingCategories.map((cat, idx) => {
                const iconName = cat.id === 'it-consultancy' ? 'Briefcase' : cat.icon;
                const IconComponent = (LucideIcons as any)[iconName] || Layout;
                const isActive = activeCategoryIndex === idx;
                const tabTitle = cat.id === 'it-consultancy' ? 'Danışmanlık' : cat.title;

                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategoryIndex(idx)}
                    className={`relative z-10 w-[110px] md:w-[180px] py-3 rounded-full font-bold text-xs md:text-sm flex items-center justify-center gap-2 transition-colors duration-300 ${
                      isActive ? 'text-white' : 'text-gray-500 hover:text-brand-dark'
                    }`}
                  >
                    <IconComponent size={16} className="md:w-[18px] md:h-[18px]" />
                    <span className="hidden md:inline">{tabTitle}</span>
                    <span className="md:hidden">{tabTitle.split(' ')[0]}</span>
                  </button>
                );
              })}
            </div>

            <button
              onClick={handleNextTab}
              className="hidden md:flex w-10 h-10 rounded-full bg-white shadow-md items-center justify-center text-brand-blue hover:bg-brand-blue hover:text-white transition-all"
            >
              <ChevronRight size={24} />
            </button>
          </FadeIn>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto items-start transition-all duration-500 mb-16">
            {currentPlans.map((plan, idx) => {
              const isDark = plan.isPopular;
              const accentColor = "text-brand-blue";
              const accentBg = "bg-brand-blue";
              const accentBorder = "border-brand-blue";

              return (
                <FadeIn
                  key={idx}
                  delay={idx * 0.15}
                  className={`relative rounded-2xl p-8 shadow-xl border flex flex-col h-full transition-all duration-300 ${isDark
                    ? `bg-brand-dark text-white ${accentBorder} transform scale-105 z-10`
                    : 'bg-white text-brand-dark border-gray-100'
                    }`}
                >

                  <div className="mb-8">
                    <div className="flex justify-start mb-4">
                      <div className={`px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2 ${isDark ? 'bg-white/10 text-white' : 'bg-brand-blue/10 text-brand-blue'
                        }`}>
                        {idx === 0 && <ThumbsUp size={14} />}
                        {idx === 1 && <ShieldCheck size={14} />}
                        {idx === 2 && <Star size={14} />}
                        {plan.badge || 'Popüler'}
                      </div>
                    </div>

                    <h3 className="text-3xl font-bold mb-4">{plan.name}</h3>

                    <p className={`text-sm leading-relaxed mb-6 min-h-[40px] ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {plan.description}
                    </p>

                    <div className="flex items-baseline gap-1 flex-wrap">
                      <span className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-brand-dark'}`}>{plan.price}</span>
                      {plan.priceSuffix && (
                        <span className={`text-xs font-bold ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>{plan.priceSuffix}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex-grow">
                    <ul className="space-y-4 mb-8">
                      {plan.features.map((feature, fIdx) => (
                        <li key={fIdx} className={`flex items-start gap-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          <div className="mt-0.5 min-w-[20px]">
                            <CheckCircle2 size={20} className={accentColor} />
                          </div>
                          <span className="leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {plan.footnote && (
                    <div className={`mt-auto mb-6 text-[11px] leading-tight italic opacity-80 ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                      {plan.footnote}
                    </div>
                  )}

                  <div className={plan.footnote ? "" : "mt-auto"}>
                    <button
                      onClick={() => navigate('/iletisim')}
                      className={`w-full py-4 rounded-xl flex items-center justify-center gap-2 transition-all font-bold ${isDark
                        ? 'bg-white text-brand-dark hover:bg-brand-blue hover:text-white shadow-lg'
                        : 'bg-brand-dark text-white hover:bg-brand-blue'
                        }`}
                    >
                      İletişime Geç
                    </button>
                  </div>
                </FadeIn>
              );
            })}
          </div>

          {/* Dynamic FAQ Section Based on Selected Category */}
          <PricingFaqSection categoryId={currentCategory?.id} />

        </div>
      </section>
    </div>
  );
};

// Sub-component for Pricing FAQs
import { PRICING_FAQS, MOBILE_APP_FAQS, ENTERPRISE_FAQS } from '../constants';
import { ChevronDown, ChevronUp } from 'lucide-react';

const PricingFaqSection = ({ categoryId }: { categoryId?: string }) => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // Determine which FAQs to show based on category
  let currentFaqs = PRICING_FAQS;
  let title = "Web Sitesi Paketleri İçin Sıkça Sorulan Sorular";

  if (categoryId === 'mobile-app') {
    currentFaqs = MOBILE_APP_FAQS;
    title = "Mobil Uygulama Paketleri İçin Sıkça Sorulan Sorular";
  } else if (categoryId === 'it-consultancy') {
    currentFaqs = ENTERPRISE_FAQS;
    title = "IT Danışmanlık ve Özel Yazılım İçin Sıkça Sorulan Sorular";
  }

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  if (!currentFaqs || currentFaqs.length === 0) return null;

  return (
    <FadeIn className="max-w-4xl mx-auto mt-20" delay={0.2}>
      <h3 className="text-2xl font-bold text-center text-brand-dark mb-8">{title}</h3>
      <div className="space-y-4">
        {currentFaqs.map((faq, idx) => (
          <div key={idx} className={`border border-gray-200 rounded-lg overflow-hidden transition-colors duration-300 ${openFaq === idx ? 'bg-[#3e73fe] border-[#3e73fe]' : 'bg-white hover:border-brand-blue/30'}`}>
            <button
              onClick={() => toggleFaq(idx)}
              className={`w-full flex justify-between items-center p-6 text-left transition-colors ${openFaq === idx ? 'text-white' : 'text-brand-dark'}`}
            >
              <span className="font-medium text-lg">{faq.question}</span>
              {openFaq === idx ? <ChevronUp className="flex-shrink-0 ml-4" /> : <ChevronDown className="flex-shrink-0 ml-4" />}
            </button>
            <div className={`transition-all duration-300 ease-in-out ${openFaq === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className={`p-6 pt-0 ${openFaq === idx ? 'text-white' : 'text-gray-700'}`}>
                {faq.answer}
              </div>
            </div>
          </div>
        ))}
      </div>
    </FadeIn>
  );
};

export default Services;
